import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge/badge';
import { AdminAccount } from '@certhon/models';
import { ChevronRight, KeyRound, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface TagAuthSectionProps {
  account: AdminAccount;
  onApproveRequest?: () => void;
  onDenyRequest?: () => void;
  onPreApprove?: () => void;
  onRemovePreApproval?: () => void;
  onDisable?: () => void;
  disabled?: boolean;
}

type TagAuthState = 'inactive' | 'pending' | 'preapproved' | 'active';

function getTagAuthState(account: AdminAccount): TagAuthState {
  if (account.tag_id) return 'active';
  if (account.requested_tag_id) return 'pending';
  if (account.tag_authentication_preapproved) return 'preapproved';
  return 'inactive';
}

export function TagAuthSection({
  account,
  onApproveRequest,
  onDenyRequest,
  onPreApprove,
  onRemovePreApproval,
  onDisable,
  disabled,
}: TagAuthSectionProps) {
  const [open, setOpen] = useState(false);
  const state = getTagAuthState(account);

  const stateConfig = {
    inactive: { label: 'Not Configured', variant: 'secondary' as const },
    pending: { label: 'Request Pending', variant: 'destructive' as const },
    preapproved: { label: 'Pre-approved', variant: 'default' as const },
    active: { label: 'Active', variant: 'positive' as const },
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border"
    >
      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center gap-2 p-3">
        <ChevronRight
          className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
        />
        <KeyRound className="h-4 w-4" />
        <span className="font-medium">Tag Authentication</span>
        <Badge variant={stateConfig[state].variant} className="ml-auto">
          {stateConfig[state].label}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 px-3 pb-3">
        {state === 'pending' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Request submitted{' '}
                {account.requested_tag_on
                  ? formatDistanceToNow(new Date(account.requested_tag_on), {
                      addSuffix: true,
                    })
                  : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={onApproveRequest}
                  disabled={disabled}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDenyRequest}
                  disabled={disabled}
                >
                  Deny
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {state === 'preapproved' && (
          <Alert>
            <AlertDescription className="flex items-center justify-between">
              <span>
                Pre-approved until{' '}
                {account.tag_authentication_preapproved_expires_on
                  ? formatDistanceToNow(
                      new Date(
                        account.tag_authentication_preapproved_expires_on,
                      ),
                      { addSuffix: true },
                    )
                  : 'indefinitely'}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={onRemovePreApproval}
                disabled={disabled}
              >
                Remove Pre-approval
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {state === 'active' && (
          <div className="text-muted-foreground text-sm">
            Tag authentication is active.
            <Button
              size="sm"
              variant="outline"
              className="ml-2"
              onClick={onDisable}
              disabled={disabled}
            >
              Disable
            </Button>
          </div>
        )}

        {state === 'inactive' && (
          <div className="text-muted-foreground text-sm">
            No tag authentication configured.
            <Button
              size="sm"
              variant="outline"
              className="ml-2"
              onClick={onPreApprove}
              disabled={disabled}
            >
              Pre-approve (90 days)
            </Button>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
